import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Modal } from 'react-bootstrap';
import * as Actions from 'redux/actions/index';
import SyllabusUploadStep1 from './SyllabusUploadStep1';
import SyllabusUploadStep2 from './SyllabusUploadStep2';
import SyllabusUploadStep3 from './SyllabusUploadStep3';
import SyllabusUploadStepDeliveryPreferences from './SyllabusUploadStepDeliveryPreferences';
import './SyllabusUpload.scss';

const DEFAULT_SOURCE_IDS = [
  '612d198036d90d23edb18e29',
  '5b9e9833c0aa700013184492',
  '6011c3dee336880017e196b0',
  '5b9b0f54d5f4a90014f3d599',
  '6148e380beafe0f769a044af',
  '6148cf62beafe0f76995cd70',
  '5bdcc04088467b0013fbad84',
  '612d198036d90d23edb18e4f',
  '5b945c625f8f1e00149e81ab',
  '626be4b5ea038ce5c4708280',
  '5b9e8b15c0aa70001318447a',
  '5b9e8e59c0aa700013184483',
  '5b9af86fd5f4a90014f3d584',
  '5b9b0bafd5f4a90014f3d594',
  '5b9b025bd5f4a90014f3d591',
  '5b9e8ab4c0aa700013184479',
];

const DEFAULT_DELIVERY_PREFERENCES = {
  deliveryDay: 'Sunday',
  reminderIfNotReviewed: true,
  showInstructorNotes: false,
  autoPublishWithoutReview: false,
  courseName: '',
  courseDescription: '',
  sources: DEFAULT_SOURCE_IDS,
};

const toTitleCase = (value = '') =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';

const getResolvedSources = (sources) =>
  Array.isArray(sources) && sources.length > 0
    ? sources
    : DEFAULT_DELIVERY_PREFERENCES.sources;

const normalizeSyllabusRows = (data) => {
  const existingRows = data?.rows || data?.syllabus?.rows || [];

  return existingRows.map((row) => ({
    _id: row._id,
    date: row.date,
    weekStartDate: row.weekStartDate,
    skipWeek: row.skipWeek === true,
    topic: row.name || row.topic || '',
    name: row.name || row.topic || '',
    description: row.description || '',
  }));
};

const normalizeDeliveryPreferences = (
  data,
  courseName = '',
  courseDescription = '',
) => {
  const syllabus = data?.syllabus || data || {};
  const settings = syllabus?.settings || data?.settings || {};
  const deliveryDay = settings.deliveryDay || syllabus.deliveryDay;

  return {
    deliveryDay: toTitleCase(
      deliveryDay || DEFAULT_DELIVERY_PREFERENCES.deliveryDay,
    ),
    reminderIfNotReviewed:
      settings.reminderIfNotReviewedByTuesday ??
      syllabus.reminderIfNotReviewedByTuesday ??
      DEFAULT_DELIVERY_PREFERENCES.reminderIfNotReviewed,
    showInstructorNotes:
      settings.showInstructorNotesToStudents ??
      syllabus.showInstructorNotesToStudents ??
      DEFAULT_DELIVERY_PREFERENCES.showInstructorNotes,
    autoPublishWithoutReview:
      settings.autoPublishWithoutReview ??
      syllabus.autoPublishWithoutReview ??
      DEFAULT_DELIVERY_PREFERENCES.autoPublishWithoutReview,
    courseName: settings.courseName || courseName || '',
    courseDescription: settings.courseDescription || courseDescription || '',
    sources: getResolvedSources(settings.sources),
  };
};

function SyllabusUploadModal({
  courseId,
  courses,
  onClose,
  actions,
  mode = 'add', // 'add' or 'edit'
  onSyllabusSaved, // Callback when syllabus is saved
}) {
  const [step, setStep] = useState(mode === 'edit' ? 2 : 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deliveryPreferences, setDeliveryPreferences] = useState(
    DEFAULT_DELIVERY_PREFERENCES,
  );
  const [savedSyllabus, setSavedSyllabus] = useState(null);
  const isEditMode = mode === 'edit';

  const course = useMemo(
    () =>
      (courses || []).find(
        (c) => c.courseId === courseId || c._id === courseId,
      ),
    [courses, courseId],
  );

  const loadExistingSyllabus = useCallback(async () => {
    try {
      const data = await actions.getSyllabus(courseId);
      const normalizedRows = normalizeSyllabusRows(data);

      if (normalizedRows.length > 0) {
        setRows(normalizedRows);
      }

      setDeliveryPreferences(
        normalizeDeliveryPreferences(
          data,
          course?.name || '',
          course?.description || '',
        ),
      );
      setSavedSyllabus(data);
    } catch (error) {
      console.error('Error loading existing syllabus:', error);
    }
  }, [actions, courseId, course]);

  // Reset state when modal is opened
  useEffect(() => {
    setStep(isEditMode ? 2 : 1);
    setRows([]);
    setLoading(false);
    setDeliveryPreferences({
      ...DEFAULT_DELIVERY_PREFERENCES,
      courseName: course?.name || '',
      courseDescription: course?.description || '',
    });
    setSavedSyllabus(null);

    if (courseId) {
      loadExistingSyllabus();
    }
  }, [courseId, isEditMode, loadExistingSyllabus, course]);

  const handleAnalyze = async (file) => {
    setLoading(true);
    try {
      const response = await actions.uploadSyllabus(courseId, file);
      const mappedRows = (response.rows || []).map((row) => ({
        _id: row._id,
        date: row.date,
        weekStartDate: row.weekStartDate,
        skipWeek: row.skipWeek === true,
        topic: row.name || row.topic || '',
        name: row.name || row.topic || '',
        description: row.description || '',
      }));
      setRows(mappedRows);
      setStep(2);
    } catch (error) {
      console.error('Error analyzing syllabus', error);
    } finally {
      setLoading(false);
    }
  };

  // Step 1 skip: go directly to step 2 with empty table
  const handleSkip = () => {
    setRows([]);
    setStep(2);
  };

  const handleConfirmSchedule = async () => {
    try {
      setLoading(true);

      const normalizedRows = rows.map((row) => ({
        date: row.date,
        name: (row.topic || row.name || '').trim(),
        description: row.description || '',
        ...(row._id ? { _id: row._id } : {}),
      }));

      const hasUnsavedRows = normalizedRows.some((row) => !row._id);

      if (hasUnsavedRows) {
        await actions.createDraftSyllabus(courseId, normalizedRows);
        const latestSyllabus = await actions.getSyllabus(courseId);
        const refreshedRows = normalizeSyllabusRows(latestSyllabus);
        setRows(refreshedRows);
      }

      setStep(3);
    } catch (error) {
      console.error('Error confirming schedule', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeliveryPreferences = async () => {
    try {
      setLoading(true);
      const settingsPayload = {
        deliveryDay: (
          deliveryPreferences.deliveryDay || 'Sunday'
        ).toLowerCase(),
        reminderIfNotReviewedByTuesday:
          deliveryPreferences.reminderIfNotReviewed,
        showInstructorNotesToStudents: deliveryPreferences.showInstructorNotes,
        autoPublishWithoutReview: deliveryPreferences.autoPublishWithoutReview,
        courseName: deliveryPreferences.courseName || '',
        courseDescription: deliveryPreferences.courseDescription || '',
        sources: getResolvedSources(deliveryPreferences.sources),
      };
      await actions.updateSyllabusSettings(courseId, settingsPayload);

      let latestSyllabus = await actions.updateSyllabusStatus(
        courseId,
        'published',
      );

      if (
        !latestSyllabus?.generatedDetails &&
        !latestSyllabus?.syllabus?.generatedDetails
      ) {
        latestSyllabus = await actions.getSyllabus(courseId);
      }

      setSavedSyllabus(latestSyllabus);

      if (onSyllabusSaved) {
        onSyllabusSaved();
      }

      setStep(4);
    } catch (error) {
      console.error('Error confirming syllabus', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onClose();
  };

  return (
    <Modal
      show={true}
      className="syllabus-upload-modal"
      dialogClassName="syllabus-upload-dialog"
    >
      <Modal.Body>
        <div className="main-padding">
          <div className="syllabus-step-progress">
            <div className="step-dots">
              <div
                className={`dot ${step > 1 ? 'done' : ''} ${step === 1 ? 'active' : ''}`}
              />
              <div
                className={`dot ${step > 2 ? 'done' : ''} ${step === 2 ? 'active' : ''}`}
              />
              <div
                className={`dot ${step > 3 ? 'done' : ''} ${step === 3 ? 'active' : ''}`}
              />
              <div
                className={`dot ${step > 4 ? 'done' : ''} ${step === 4 ? 'active' : ''}`}
              />
            </div>
          </div>
          {step === 1 && (
            <SyllabusUploadStep1
              onAnalyze={handleAnalyze}
              onSkip={handleSkip}
              onCancel={onClose}
              loading={loading}
            />
          )}
          {step === 2 && (
            <SyllabusUploadStep2
              rows={rows}
              onChange={setRows}
              onBack={isEditMode ? onClose : () => setStep(1)}
              onConfirm={handleConfirmSchedule}
              loading={loading}
              courseId={courseId}
              deliveryDay={deliveryPreferences.deliveryDay}
              backLabel={isEditMode ? 'Cancel' : 'Back'}
              syllabusData={savedSyllabus?.syllabus || {}}
            />
          )}
          {step === 3 && (
            <SyllabusUploadStepDeliveryPreferences
              courseId={courseId}
              value={deliveryPreferences}
              onChange={setDeliveryPreferences}
              onBack={() => setStep(2)}
              onConfirm={handleConfirmDeliveryPreferences}
              loading={loading}
            />
          )}
          {step === 4 && (
            <SyllabusUploadStep3
              rows={rows}
              course={course}
              deliveryPreferences={deliveryPreferences}
              syllabusData={savedSyllabus}
              onDone={handleDone}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

function mapStateToProps(state) {
  return {
    courses: state.classRoom.courses || [],
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SyllabusUploadModal);
